import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { Button, Col, OutlinedInput, Row, RSelect } from "../../Component";
import FormWithLabel from "../../input/label/FormWithLabel";
import { USER_ROLES } from "../../../utilities/constants";

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const rolesEqual = (a, b) =>
  JSON.stringify((a ?? []).slice().sort()) ===
  JSON.stringify((b ?? []).slice().sort());

const norm = (v) => (v ?? "").trim();

const UserEditModal = ({ showModal, user, onClose, onSave, isSaving }) => {
  const { t } = useTranslation();

  const { register, handleSubmit, reset, watch, formState } = useForm();
  const errors = formState?.errors || {};

  const [selectedRoles, setSelectedRoles] = useState([]);
  const [rolesError, setRolesError] = useState("");
  const initialValuesRef = useRef(null);

  const roleOptions = useMemo(
    () =>
      Object.values(USER_ROLES).map((role) => ({
        value: role,
        label: role,
      })),
    []
  );

  useEffect(() => {
    if (showModal && user) {
      const initial = {
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        email: user.email ?? "",
        roles: (user.roles ?? []).slice(),
      };

      initialValuesRef.current = initial;

      reset({
        firstName: initial.firstName,
        lastName: initial.lastName,
        email: initial.email,
      });

      setSelectedRoles(initial.roles.map((r) => ({ value: r, label: r })));
      setRolesError("");
    }
  }, [showModal, user, reset]);

  useEffect(() => {
    if (!showModal) {
      initialValuesRef.current = null;
      setSelectedRoles([]);
      setRolesError("");
    }
  }, [showModal]);

  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const email = watch("email");

  const currentRoles = useMemo(
    () => (selectedRoles ?? []).map((r) => r.value),
    [selectedRoles]
  );

  const hasChanges = useMemo(() => {
    const init = initialValuesRef.current;
    if (!init) return false;

    return (
      norm(firstName) !== norm(init.firstName) ||
      norm(lastName) !== norm(init.lastName) ||
      norm(email) !== norm(init.email) ||
      !rolesEqual(currentRoles, init.roles)
    );
  }, [firstName, lastName, email, currentRoles]);

  async function onFormSubmit(form) {
    setRolesError("");

    const roles = (selectedRoles ?? []).map((r) => r.value);
    if (!roles.length) {
      setRolesError(t("At least one role is required"));
      return;
    }

    const body = {
      id: user.id,
      firstName: norm(form.firstName),
      lastName: norm(form.lastName),
      email: norm(form.email),
      roles,
    };

    await onSave(body);
  }

  return (
    <Modal isOpen={showModal}>
      <ModalHeader
        toggle={onClose}
        close={<Button className="close" onClick={onClose} iconName="cross" />}
      >
        {t("Edit User")}
      </ModalHeader>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <ModalBody>
          <Row className="g-3">
            <Col size="12">
              <FormWithLabel label="First Name" noWrap>
                <OutlinedInput
                  type="text"
                  className="form-control"
                  label="First Name"
                  name="firstName"
                  forwardRef={register({
                    required: t("This field is required"),
                    minLength: {
                      value: 2,
                      message: t("At least 2 characters required"),
                    },
                  })}
                  errors={errors}
                />
              </FormWithLabel>
            </Col>

            <Col size="12">
              <FormWithLabel label="Last Name" noWrap>
                <OutlinedInput
                  type="text"
                  className="form-control"
                  label="Last Name"
                  name="lastName"
                  forwardRef={register({
                    required: t("This field is required"),
                    minLength: {
                      value: 2,
                      message: t("At least 2 characters required"),
                    },
                  })}
                  errors={errors}
                />
              </FormWithLabel>
            </Col>

            <Col size="12">
              <FormWithLabel label="Email" noWrap>
                <OutlinedInput
                  type="email"
                  className="form-control"
                  label="Email"
                  name="email"
                  forwardRef={register({
                    required: t("This field is required"),
                    pattern: {
                      value: EMAIL_PATTERN,
                      message: t("Invalid email address"),
                    },
                  })}
                  errors={errors}
                />
              </FormWithLabel>
            </Col>

            <Col size="12">
              <FormWithLabel label="Roles" noWrap>
                <RSelect
                  options={roleOptions}
                  isMulti
                  value={selectedRoles}
                  onChange={(e) => {
                    setSelectedRoles(e ?? []);
                    if (rolesError) setRolesError("");
                  }}
                />
                {rolesError ? (
                  <p className="invalid mt-1 mb-0">{rolesError}</p>
                ) : null}
              </FormWithLabel>
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter className="d-flex justify-content-between">
          <Button
            type="button"
            color="white border-light"
            onClick={onClose}
            title={t("Close")}
          />

          <Button
            type="submit"
            color="primary"
            iconName="save"
            title={t("Save")}
            isLoading={isSaving}
            disabled={!hasChanges || isSaving}
          />
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default UserEditModal;
